import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MdClose } from 'react-icons/md';
import DualDatePicker from '../common/DualDatePicker';
import AddChargeForm from '../common/AddChargeForm';
import { recordOtherIncome } from '../../services/financeService';
import { getStudentsForPayment } from '../../services/feeService';

// Income categories (mirror OtherIncome.category enum) + payment methods (FeePayment enum).
const CATEGORIES = [
  ['uniform_sale', 'Uniform sale'],
  ['drop_in', 'Drop-in fee'],
  ['donation', 'Donation'],
  ['equipment_sale', 'Equipment sale'],
  ['other', 'Other'],
];
const METHODS = [
  ['cash', 'Cash'], ['bank', 'Bank transfer'], ['esewa', 'eSewa'],
  ['khalti', 'Khalti'], ['fonepay', 'Fonepay'], ['other', 'Other'],
];

const todayIso = () => {
  const k = new Date(Date.now() + (5 * 60 + 45) * 60 * 1000); // Kathmandu today
  return `${k.getUTCFullYear()}-${String(k.getUTCMonth() + 1).padStart(2, '0')}-${String(k.getUTCDate()).padStart(2, '0')}`;
};

// One modal, two tabs: money NOT tied to a student (this form, unchanged) vs. a charge FOR a
// student (AddChargeForm, shared with the Students drawer / Payments Collect tab — see that
// file). Two genuinely separate pipelines underneath (OtherIncome vs. FeePayment{type:'custom'})
// — this just gives the second one a front door from Finance too.
export default function RecordIncomeModal({ onClose, onSaved }) {
  const [tab, setTab] = useState('other'); // 'other' | 'student'
  const [form, setForm] = useState({
    amount: '', category: 'uniform_sale', description: '', method: 'cash', receivedDate: todayIso(), payerName: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Fetched lazily — only once the "For a student" tab is actually opened. Same query key
  // Payments.jsx's Collect tab uses, so the cache is shared if either was already loaded.
  const { data: chargeStudentsData, isLoading: loadingStudents } = useQuery({
    queryKey: ['fee-students'],
    queryFn:  () => getStudentsForPayment().then(r => r.data),
    enabled:  tab === 'student',
  });
  const chargeStudents = chargeStudentsData?.students || [];

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setError('');
    if (!form.description.trim()) return setError('Please add a short description.');
    if (!(Number(form.amount) > 0)) return setError('Enter an amount greater than 0.');
    if (!form.receivedDate) return setError('Pick the date the money came in.');
    setSaving(true);
    try {
      await recordOtherIncome({
        amount: Number(form.amount), category: form.category, description: form.description.trim(),
        method: form.method, receivedDate: form.receivedDate,
        payerName: form.payerName.trim() || undefined, notes: form.notes.trim() || undefined,
      });
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not save. Please try again.');
      setSaving(false);
    }
  };

  const fieldCls = 'w-full min-h-[44px] rounded-xl border border-divider bg-card text-secondary text-sm px-3 focus:outline-none focus:ring-2 focus:ring-primary/30';
  const labelCls = 'block text-xs font-medium text-muted mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-0 md:p-4" onMouseDown={onClose}>
      <div
        className="bg-card w-full md:max-w-md rounded-t-2xl md:rounded-2xl border border-divider shadow-xl max-h-[92vh] overflow-y-auto"
        onMouseDown={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-divider sticky top-0 bg-card">
          <h3 className="text-sm font-semibold text-secondary">Record income</h3>
          <button type="button" onClick={onClose} className="text-muted hover:text-secondary" aria-label="Close"><MdClose className="text-xl" /></button>
        </div>

        <div className="flex border-b border-divider sticky top-[49px] bg-card">
          {[
            ['other',   'Not tied to a student'],
            ['student', 'For a student'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`flex-1 py-2.5 text-xs font-semibold transition-colors border-b-2 ${
                tab === key ? 'border-primary text-primary' : 'border-transparent text-subtle hover:text-muted'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'student' ? (
          <AddChargeForm
            students={chargeStudents}
            studentsLoading={loadingStudents}
            onClose={onClose}
            onCreated={onSaved}
          />
        ) : (
        <>
        <div className="p-4 space-y-4">
          <p className="text-xs text-subtle">For money not tied to a student — a walk-in uniform sale, a drop-in fee, a donation.</p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Amount (NPR)</label>
              <input type="number" min="0" step="0.01" inputMode="decimal" value={form.amount} onChange={e => set('amount', e.target.value)} className={fieldCls} autoFocus />
            </div>
            <div>
              <label className={labelCls}>What for</label>
              <select value={form.category} onChange={e => set('category', e.target.value)} className={fieldCls}>
                {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>Description</label>
            <input type="text" value={form.description} onChange={e => set('description', e.target.value)} placeholder="e.g. Uniform sold to walk-in" className={fieldCls} maxLength={200} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Received by</label>
              <select value={form.method} onChange={e => set('method', e.target.value)} className={fieldCls}>
                {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Date received</label>
              <DualDatePicker value={form.receivedDate} onChange={v => set('receivedDate', v)} max={todayIso()} />
            </div>
          </div>

          <details className="text-sm">
            <summary className="text-xs text-primary cursor-pointer select-none">Add payer name / notes (optional)</summary>
            <div className="mt-3 space-y-3">
              <input type="text" value={form.payerName} onChange={e => set('payerName', e.target.value)} placeholder="Payer name (optional)" className={fieldCls} maxLength={100} />
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Notes (optional)" rows={2} className={`${fieldCls} py-2 min-h-[60px]`} maxLength={1000} />
            </div>
          </details>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-divider sticky bottom-0 bg-card">
          <button type="button" onClick={onClose} className="min-h-[40px] px-4 rounded-full text-sm font-medium text-muted hover:text-secondary">Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className="min-h-[40px] px-5 rounded-full bg-primary text-white text-sm font-medium hover:opacity-90 transition disabled:opacity-50">
            {saving ? 'Saving…' : 'Record income'}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
